import { Sidebar } from "primereact/sidebar";
import { ClientId, featureEnabled } from "../../../client-features";
import { EffectiveDateFilter } from "../../../components/filters/EffectiveDateFilter";
import {
  AssigneeFilterDropdown,
  FilterDropdown,
  StatusFilterDropdown,
  UserOption,
} from "../../../components/filters/Filters";
import { GoogleGroupIcon } from "../../../components/google-group/GoogleGroupIcon";
import { IfFeatureEnabled } from "../../../IfFeatureEnabled";
import { ReportStatus } from "../../../api/types";
import { Filter, updatedFilter } from "./filter";
import { FilterOption } from "../../../components/filters/Filters";
import styles from "./MobileFilterSidebar.module.css";

type StatusOption = { name: string; code: ReportStatus };
type AppetiteOption = { name: string; code: number };

export const MobileFilterSidebar = ({
  visible,
  onHide,
  clientId,
  filter,
  onFilterChange,
  statuses,
  selectedStatuses,
  selectedAssigneeUsers,
  selectedHandlerUsers,
  appetites,
  selectedAppetites,
  brokerTeams,
  selectedBrokerTeams,
}: {
  visible: boolean;
  onHide: () => void;
  clientId: ClientId;
  filter: Filter;
  onFilterChange: (filter: Filter) => void;
  statuses: StatusOption[];
  selectedStatuses: StatusOption[];
  selectedAssigneeUsers: UserOption[];
  selectedHandlerUsers: UserOption[];
  appetites: AppetiteOption[];
  selectedAppetites: AppetiteOption[];
  brokerTeams: FilterOption<string>[];
  selectedBrokerTeams: FilterOption<string>[];
}) => {
  return (
    <Sidebar
      visible={visible}
      position="bottom"
      onHide={onHide}
      header="Filters"
      style={{ height: "auto", maxHeight: "85vh" }}
    >
      <div className={styles.filterSheet}>
        <div className={styles.filterField}>
          <span className={styles.filterLabel}>Status</span>
          <StatusFilterDropdown
            isTransparent={false}
            statuses={statuses}
            selectedStatuses={selectedStatuses}
            setSelectedStatuses={(statuses) =>
              onFilterChange(
                updatedFilter(
                  "selectedStatuses",
                  statuses.map((s) => s.code),
                  filter
                )
              )
            }
          />
        </div>
        <div className={styles.filterField}>
          <span className={styles.filterLabel}>
            {featureEnabled(clientId, "assignee-named-underwriter")
              ? "Underwriter"
              : "Assignee"}
          </span>
          <AssigneeFilterDropdown
            isTransparent={false}
            icon="pi pi-user-edit"
            selectedUsers={selectedAssigneeUsers}
            setSelectedUsers={(users) =>
              onFilterChange(
                updatedFilter(
                  "selectedAssigneeUserIds",
                  users.map((u) => u.id),
                  filter
                )
              )
            }
          />
        </div>
        {featureEnabled(clientId, "handler-column") && (
          <div className={styles.filterField}>
            <span className={styles.filterLabel}>Specialist</span>
            <AssigneeFilterDropdown
              isTransparent={false}
              icon="pi pi-users"
              selectedUsers={selectedHandlerUsers}
              setSelectedUsers={(users) =>
                onFilterChange(
                  updatedFilter(
                    "selectedHandlerUserIds",
                    users.map((u) => u.id),
                    filter
                  )
                )
              }
            />
          </div>
        )}
        <IfFeatureEnabled feature="appetite-filtering">
          <div className={styles.filterField}>
            <span className={styles.filterLabel}>Appetite</span>
            <FilterDropdown
              isTransparent={false}
              options={appetites}
              selectedOptions={selectedAppetites}
              setSelectedOptions={(appetites) =>
                onFilterChange(
                  updatedFilter(
                    "selectedAppetites",
                    appetites.map((a) => a.code.toString()),
                    filter
                  )
                )
              }
            />
          </div>
        </IfFeatureEnabled>
        <div className={styles.filterField}>
          <span className={styles.filterLabel}>Broker Team</span>
          <FilterDropdown
            isTransparent={false}
            options={brokerTeams}
            selectedOptions={selectedBrokerTeams}
            setSelectedOptions={(teams) =>
              onFilterChange(
                updatedFilter(
                  "selectedBrokerTeams",
                  teams.map((t) => t.code),
                  filter
                )
              )
            }
            itemTemplate={(option) => (
              <GoogleGroupIcon groupName={option.code} alwaysShowName />
            )}
          />
        </div>
        <div className={styles.filterField}>
          <span className={styles.filterLabel}>Effective Date</span>
          <EffectiveDateFilter
            effectiveDateStart={filter.effectiveDateStart}
            effectiveDateEnd={filter.effectiveDateEnd}
            onChangeStart={(value) =>
              onFilterChange(updatedFilter("effectiveDateStart", value, filter))
            }
            onChangeEnd={(value) =>
              onFilterChange(updatedFilter("effectiveDateEnd", value, filter))
            }
            onClearBoth={() =>
              onFilterChange(
                updatedFilter(
                  "effectiveDateEnd",
                  "",
                  updatedFilter("effectiveDateStart", "", filter)
                )
              )
            }
            onChangeBoth={(start, end) =>
              onFilterChange(
                updatedFilter(
                  "effectiveDateEnd",
                  end,
                  updatedFilter("effectiveDateStart", start, filter)
                )
              )
            }
          />
        </div>
      </div>
    </Sidebar>
  );
};
